import React, { useMemo } from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import config from '../../config/environments';
import { normalizeWineColor, wineColorDotFor, wineColorLabelFor } from '../../utils/wineColor';
import type { MenuPublicWine } from '../../types/menu';

/**
 * Customer-facing menu (Phase 2C.b, extended in Phase 4.B.c for WINE).
 *
 * Renders the public DTO returned by /api/public/menu/:slug (or by the
 * unified resolver at /api/public/m/:slug when `kind === 'menu'`).
 *
 * Visual language matches the wine-list public view — dark restaurant
 * palette, serif typography on item names, gold accents. COCKTAIL items
 * use a simple title/subtitle/price card; WINE items (Phase 4.B.c) also
 * surface vintage, producer · region, color, scores, and bottle size,
 * sourced strictly from the public `wine` sub-DTO — never admin-only
 * fields like binNumber, inventoryItemId, consumptionRecipeId.
 *
 * The DTO is built by explicit field projection on the backend, so this
 * view only needs to render what's present; nothing admin-only is ever
 * available here even by mistake.
 */

interface PublicMenuFormat {
  name: string;
  formatKind: 'VOLUME' | 'COURSE' | 'UNIT' | 'PORTION';
  volumeMl: number | null;
  price: number;
}

/**
 * Public item DTO. `kind` and `wine` are only present on WINE items
 * (backend emits them via explicit projection — see publicMenuController).
 * The `wine` block uses the shared `MenuPublicWine` type so the customer
 * surface and the type contract stay in sync.
 */
interface PublicMenuItem {
  id: string;
  name: string | null;
  subtitle: string | null;
  basePrice: number | null;
  currency: string | null;
  notes: string | null;
  externalUrl: string | null;
  formats: PublicMenuFormat[];
  kind?: 'WINE';
  wine?: MenuPublicWine;
}

interface PublicMenuSection {
  id: string | null;
  name: string;
  description: string | null;
  items: PublicMenuItem[];
}

export interface PublicMenuPayload {
  menu: {
    slug: string;
    name: string;
    type: string;
    description: string | null;
    venueName: string | null;
    venueLogoUrl: string | null;
    defaultCurrency: string | null;
  };
  sections: PublicMenuSection[];
}

interface Props {
  data: PublicMenuPayload;
}

/**
 * Resolve a backend-relative logo URL into an absolute browser URL.
 * Mirrors the wine-list public view's resolver (inlined here to avoid
 * importing from PreviewMock, which is a wine-list-specific module).
 */
const resolveLogoUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const apiOrigin = config.apiUrl.replace(/\/api\/?$/, '');
  return url.startsWith('/') ? `${apiOrigin}${url}` : `${apiOrigin}/${url}`;
};

const formatPrice = (price: number | null, currency: string | null): string => {
  if (price == null) return '—';
  const fixed = price.toFixed(2);
  return currency ? `${currency} ${fixed}` : fixed;
};

const PublicMenu: React.FC<Props> = ({ data }) => {
  const { menu, sections } = data;

  // Cache-bust the venue logo with the menu slug — stable per menu so the
  // browser can cache between renders, but still avoids stale 404s after a
  // logo upload/republish.
  const logoSrc = useMemo(() => {
    const resolved = resolveLogoUrl(menu.venueLogoUrl);
    if (!resolved) return null;
    const sep = resolved.includes('?') ? '&' : '?';
    return `${resolved}${sep}v=${encodeURIComponent(menu.slug)}`;
  }, [menu.venueLogoUrl, menu.slug]);

  const isTablet = useMediaQuery('(min-width: 900px)');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
        pb: 8,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backgroundColor: 'background.default',
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          px: { xs: 2.5, md: 4 },
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt={menu.venueName ?? menu.name}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              sx={{ height: 44, width: 'auto', maxWidth: 180, objectFit: 'contain' }}
            />
          ) : (
            menu.venueName && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {menu.venueName}
              </Typography>
            )
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
                fontSize: { xs: '1.5rem', md: '1.9rem' },
                fontWeight: 600,
                lineHeight: 1.1,
                color: 'text.primary',
              }}
              noWrap
            >
              {menu.name}
            </Typography>
            {menu.description && (
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.85rem', md: '0.9rem' },
                  mt: 0.5,
                  maxWidth: 720,
                }}
              >
                {menu.description}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Sections */}
      <Box sx={{ px: { xs: 2.5, md: 4 }, pt: 3 }}>
        {(() => {
          // Empty when there are no sections OR no items across any section.
          // Either way the customer sees the same friendly placeholder.
          const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
          if (sections.length === 0 || totalItems === 0) {
            return (
              <Paper
                variant="outlined"
                sx={{
                  mt: 6,
                  mx: 'auto',
                  maxWidth: 400,
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
                    fontSize: '1.6rem',
                    color: 'text.primary',
                    mb: 1.5,
                    lineHeight: 1.2,
                  }}
                >
                  Menu coming soon
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                  This menu is being prepared. Please check back later.
                </Typography>
              </Paper>
            );
          }
          return (
            <Stack spacing={isTablet ? 5 : 4}>
              {sections.map(section => (
                <Section key={section.id ?? 'other'} section={section} isTablet={isTablet} menuCurrency={menu.defaultCurrency} />
              ))}
            </Stack>
          );
        })()}
      </Box>
    </Box>
  );
};

interface SectionProps {
  section: PublicMenuSection;
  isTablet: boolean;
  menuCurrency: string | null;
}

const Section: React.FC<SectionProps> = ({ section, isTablet, menuCurrency }) => (
  <Box>
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'primary.main',
          fontWeight: 700,
        }}
      >
        {section.name}
      </Typography>
      {section.description && (
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: '0.85rem',
            mt: 0.75,
            maxWidth: 640,
          }}
        >
          {section.description}
        </Typography>
      )}
      <Divider sx={{ mt: 1.5, opacity: 0.4 }} />
    </Box>

    {section.items.length === 0 ? (
      <Typography
        sx={{
          color: 'text.secondary',
          fontStyle: 'italic',
          fontSize: '0.85rem',
        }}
      >
        Nothing in this section yet.
      </Typography>
    ) : (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isTablet ? '1fr 1fr' : '1fr',
          columnGap: 4,
          rowGap: 3,
        }}
      >
        {section.items.map(item => (
          <ItemCard key={item.id} item={item} menuCurrency={menuCurrency} />
        ))}
      </Box>
    )}
  </Box>
);

interface ItemCardProps {
  item: PublicMenuItem;
  menuCurrency: string | null;
}

/**
 * Title shown for WINE items: prefers `wine.wineDisplay.name` (the snapshot
 * captured at the time the wine was added to the menu) over the generic
 * `item.name`, since the wine snapshot is closer to the canonical wine label.
 */
const wineTitleFor = (item: PublicMenuItem): string => {
  if (item.kind === 'WINE' && item.wine?.wineDisplay?.name) {
    return item.wine.wineDisplay.name;
  }
  return item.name ?? 'Untitled';
};

/**
 * "Producer · Region" line for a WINE item, with either piece skippable
 * when the snapshot is missing it. Returns null if neither is available.
 */
const wineProducerRegionFor = (wine: MenuPublicWine | undefined): string | null => {
  const wd = wine?.wineDisplay;
  if (!wd) return null;
  const bits = [wd.producerName, wd.regionName].filter((s): s is string => !!s && s.trim().length > 0);
  return bits.length > 0 ? bits.join(' · ') : null;
};

const ItemCard: React.FC<ItemCardProps> = ({ item, menuCurrency }) => {
  const itemCurrency = item.currency ?? menuCurrency;
  const isWine = item.kind === 'WINE';
  const wine = isWine ? item.wine : undefined;
  const producerRegion = wineProducerRegionFor(wine);
  const colorCanonical = isWine ? normalizeWineColor(wine?.wineDisplay?.color) : null;
  const colorLabel = colorCanonical ? wineColorLabelFor(wine?.wineDisplay?.color) : null;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
                fontSize: { xs: '1.2rem', md: '1.3rem' },
                fontWeight: 600,
                lineHeight: 1.2,
                color: 'text.primary',
              }}
            >
              {wineTitleFor(item)}
            </Typography>
            {isWine && wine?.wineDisplay?.displayVintage && (
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  color: 'text.secondary',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                }}
              >
                {wine.wineDisplay.displayVintage}
              </Typography>
            )}
          </Box>
          {isWine && producerRegion && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.85rem', md: '0.9rem' },
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {producerRegion}
            </Typography>
          )}
          {/* Cocktail-style subtitle. For WINE we suppress it because the
              admin-set subtitle is already producer · region — rendering it
              twice would be noisy. */}
          {!isWine && item.subtitle && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.85rem', md: '0.9rem' },
                mt: 0.5,
                lineHeight: 1.4,
              }}
            >
              {item.subtitle}
            </Typography>
          )}
          {isWine && (colorCanonical || (wine?.scores?.length ?? 0) > 0 || wine?.bottleSize != null) && (
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}
            >
              {colorCanonical && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: wineColorDotFor(wine?.wineDisplay?.color),
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      color: 'text.secondary',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {colorLabel}
                  </Typography>
                </Box>
              )}
              {wine?.bottleSize != null && (
                <Chip
                  size="small"
                  label={`${wine.bottleSize} ml`}
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    height: 20,
                    borderColor: theme => theme.palette.divider,
                    color: 'text.secondary',
                  }}
                />
              )}
              {(wine?.scores ?? []).map(s => (
                <Chip
                  key={s.source}
                  size="small"
                  label={`${s.source} ${s.score}`}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    height: 20,
                    backgroundColor: theme => theme.palette.action.hover,
                    color: 'text.primary',
                  }}
                />
              ))}
            </Stack>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              fontWeight: 600,
              color: 'primary.main',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {formatPrice(item.basePrice, itemCurrency)}
          </Typography>
        </Box>
      </Box>

      {item.formats.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 1.25, pl: 0.5 }}>
          {item.formats.map((f, idx) => (
            <Box
              key={`${item.id}-fmt-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1.5,
                color: 'text.secondary',
                fontSize: '0.85rem',
              }}
            >
              <Typography sx={{ fontWeight: 500, color: 'text.primary', minWidth: 90, fontSize: '0.85rem' }}>
                {f.name}
              </Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                {f.formatKind === 'VOLUME' && f.volumeMl != null ? `${f.volumeMl} ml` : ''}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.primary' }}>
                {formatPrice(f.price, itemCurrency)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {item.notes && (
        <Typography
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            fontSize: '0.8rem',
            mt: 1.25,
            lineHeight: 1.5,
            maxWidth: 460,
          }}
        >
          {item.notes}
        </Typography>
      )}

      {item.externalUrl && (
        <Typography
          component="a"
          href={item.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-block',
            mt: 1.25,
            fontSize: '0.75rem',
            color: 'primary.main',
            textDecoration: 'none',
            borderBottom: theme => `1px solid ${theme.palette.primary.main}`,
            pb: 0.25,
            '&:hover': { opacity: 0.85 },
          }}
        >
          View details
        </Typography>
      )}
    </Box>
  );
};

export default PublicMenu;
